import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { branchAPI } from '../../services/api';
import { saveAs } from 'file-saver';
import SuccessModal from '../Common/SuccessModal';
import ConfirmModal from '../Common/ConfirmModal';
import ErrorModal from '../Common/ErrorModal';
import {
  AdminPage,
  PageToolbar,
  ExportMenu,
  CardGrid,
  EntityCard,
  Pagination,
} from '../Common/admin';

const PAGE_SIZE = 12;

const BranchList = () => {
  const { t } = useTranslation();
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmData, setConfirmData] = useState(null);
  const fileInputRef = React.useRef(null);

  const loadBranches = useCallback(async () => {
    try {
      setLoading(true);
      const response = await branchAPI.getAll(appliedSearch, page, PAGE_SIZE);
      setBranches(response.data.items || []);
      setTotalPages(response.data.totalPages || 1);
      setTotalCount(response.data.totalCount ?? 0);
      setSelectedIds([]);
    } catch (err) {
      setErrorMessage(t('branch.list.messages.loadError'));
      setShowErrorModal(true);
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [appliedSearch, page, t]);

  useEffect(() => {
    loadBranches();
  }, [loadBranches]);

  const handleSearch = () => {
    setPage(1);
    setAppliedSearch(searchTerm);
  };

  const handleDelete = (id) => {
    setConfirmAction(() => async () => {
      try {
        await branchAPI.delete(id);
        setSuccessMessage(t('branch.list.messages.deleteSuccess'));
        setShowSuccessModal(true);
        loadBranches();
      } catch (err) {
        setErrorMessage(t('branch.list.messages.deleteError'));
        setShowErrorModal(true);
      }
    });
    setConfirmData({
      title: t('common.confirm'),
      message: t('branch.list.messages.deleteConfirm'),
      confirmText: t('common.delete'),
      cancelText: t('common.cancel'),
    });
    setShowConfirmModal(true);
  };

  const handleBulkDelete = () => {
    if (selectedIds.length === 0) {
      setErrorMessage(t('branch.list.messages.selectToDelete'));
      setShowErrorModal(true);
      return;
    }

    setConfirmAction(() => async () => {
      try {
        const response = await branchAPI.bulkDelete(selectedIds);
        setSuccessMessage(
          response.data.message ||
            t('branch.list.messages.bulkDeleteSuccess', { count: selectedIds.length })
        );
        setShowSuccessModal(true);
        setSelectedIds([]);
        loadBranches();
      } catch (err) {
        setErrorMessage(
          err.response?.data?.message || t('branch.list.messages.bulkDeleteError')
        );
        setShowErrorModal(true);
      }
    });
    setConfirmData({
      title: t('common.confirm'),
      message: t('branch.list.messages.bulkDeleteConfirm', { count: selectedIds.length }),
      confirmText: t('common.delete'),
      cancelText: t('common.cancel'),
    });
    setShowConfirmModal(true);
  };

  const handleConfirm = async () => {
    setShowConfirmModal(false);
    if (confirmAction) await confirmAction();
    setConfirmAction(null);
    setConfirmData(null);
  };

  const handleCancel = () => {
    setShowConfirmModal(false);
    setConfirmAction(null);
    setConfirmData(null);
  };

  const handleSelectAll = (e) => {
    setSelectedIds(e.target.checked ? branches.map((b) => b.id) : []);
  };

  const handleSelectOne = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const handleExport = async (format) => {
    try {
      let response;
      let filename;
      let contentType;
      const stamp = new Date().toISOString().slice(0, 10);

      switch (format) {
        case 'excel':
          response = await branchAPI.exportExcel();
          filename = `Branches_${stamp}.xlsx`;
          contentType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
          break;
        case 'csv':
          response = await branchAPI.exportCsv();
          filename = `Branches_${stamp}.csv`;
          contentType = 'text/csv;charset=utf-8';
          break;
        case 'pdf':
          response = await branchAPI.exportPdf();
          filename = `Branches_${stamp}.pdf`;
          contentType = 'application/pdf';
          break;
        default:
          return;
      }

      saveAs(new Blob([response.data], { type: contentType }), filename);
      setSuccessMessage(
        t('branch.list.messages.exportSuccess', { format: format.toUpperCase() })
      );
      setShowSuccessModal(true);
    } catch (err) {
      console.error('Export error:', err);
      setErrorMessage(t('branch.list.messages.exportError', { format: format.toUpperCase() }));
      setShowErrorModal(true);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await branchAPI.downloadTemplate();
      saveAs(response.data, `BranchesTemplate_${new Date().toISOString().slice(0, 10)}.xlsx`);
      setSuccessMessage(t('branch.list.messages.templateDownloadSuccess'));
      setShowSuccessModal(true);
    } catch (err) {
      console.error('Download template error:', err);
      setErrorMessage(t('branch.list.messages.templateDownloadError'));
      setShowErrorModal(true);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      setErrorMessage(t('branch.list.messages.importError'));
      setShowErrorModal(true);
      e.target.value = '';
      return;
    }

    try {
      const response = await branchAPI.uploadExcel(file);
      setSuccessMessage(
        response.data.message ||
          t('branch.list.messages.importSuccess', { count: response.data.count || 0 })
      );
      setShowSuccessModal(true);
      loadBranches();
    } catch (err) {
      console.error('Import error:', err);
      setErrorMessage(err.response?.data?.message || t('branch.list.messages.importFailed'));
      setShowErrorModal(true);
    } finally {
      e.target.value = '';
    }
  };

  const toolbarActions = (
    <>
      <ExportMenu onExport={handleExport} />
      <button
        type="button"
        className="ad-icon-btn"
        title={t('common.import')}
        onClick={() => fileInputRef.current?.click()}
      >
        <i className="fas fa-file-import"></i>
      </button>
      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xls"
        style={{ display: 'none' }}
        onChange={handleFileUpload}
      />
      <button
        type="button"
        className="ad-icon-btn"
        title={t('common.downloadTemplate')}
        onClick={handleDownloadTemplate}
      >
        <i className="fas fa-download"></i>
      </button>
      <button
        type="button"
        className="ad-icon-btn ad-icon-btn-danger"
        title={t('common.bulkDelete')}
        onClick={handleBulkDelete}
        disabled={selectedIds.length === 0}
      >
        <i className="fas fa-trash"></i>
      </button>
    </>
  );

  return (
    <AdminPage title={t('branch.list.title')}>
      <PageToolbar
        addTo="/branch/create"
        addLabel={t('branch.list.addNew')}
        searchValue={searchTerm}
        searchPlaceholder={t('branch.list.searchPlaceholder')}
        onSearchChange={setSearchTerm}
        onSearchSubmit={handleSearch}
        selectAll={{
          checked: selectedIds.length === branches.length && branches.length > 0,
          onChange: handleSelectAll,
          count: selectedIds.length,
        }}
        actions={toolbarActions}
      />

      <CardGrid
        loading={loading}
        isEmpty={branches.length === 0}
        emptyTitle={t('branch.list.messages.noData')}
        emptyText={t('common.noResultsHint')}
        skeletonCount={PAGE_SIZE}
      >
        {branches.map((branch) => (
          <EntityCard
            key={branch.id}
            title={branch.branchNameAr}
            subtitle={branch.branchNameEng}
            code={branch.branchCode}
            selected={selectedIds.includes(branch.id)}
            onSelect={() => handleSelectOne(branch.id)}
            fields={[
              {
                label: t('branch.list.columns.organization'),
                value: branch.organization?.organizationNameEng,
              },
              { label: t('branch.list.columns.country'), value: branch.country?.nameEng },
              { label: t('branch.list.columns.city'), value: branch.city?.cityNameEng },
              { label: t('branch.list.columns.email'), value: branch.email },
              {
                label: t('branch.list.columns.vatRatio'),
                value: branch.vatRatio ? `${branch.vatRatio}%` : null,
              },
            ]}
            badges={[
              {
                label: branch.isActive ? t('common.active') : t('common.inactive'),
                tone: branch.isActive ? 'success' : 'danger',
              },
            ]}
            viewTo={`/branch/view/${branch.id}`}
            editTo={`/branch/edit/${branch.id}`}
            onDelete={() => handleDelete(branch.id)}
          />
        ))}
      </CardGrid>

      {!loading && branches.length > 0 && (
        <Pagination
          page={page}
          totalPages={totalPages}
          totalCount={totalCount}
          pageSize={PAGE_SIZE}
          onChange={setPage}
        />
      )}

      <ErrorModal
        show={showErrorModal}
        message={errorMessage}
        onClose={() => setShowErrorModal(false)}
      />
      <SuccessModal
        show={showSuccessModal}
        message={successMessage}
        onClose={() => setShowSuccessModal(false)}
      />
      <ConfirmModal
        show={showConfirmModal}
        title={confirmData?.title}
        message={confirmData?.message}
        confirmText={confirmData?.confirmText}
        cancelText={confirmData?.cancelText}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </AdminPage>
  );
};

export default BranchList;
